import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { fetchVehicleImage } from '../api/vehicle-image.api'

import { useEffect, useState } from 'react'
import { TimeInMilliseconds } from 'src/Libs/types'
import { usePortalQuery } from 'src/Libs/api'

interface UseVehicleImage {
  imageUrl: string | null
  loading: boolean
  hasError: boolean
}

const MAX_RETRY_ATTEMPTS = 1

export const getVehicleImageQueryKey = (installationId?: string, vehicleId?: string): (string | undefined)[] => [
  'vehicle-integration',
  'image',
  vehicleId,
  installationId,
]

export const useVehicleImage = (installationId?: string, vehicleId?: string): UseVehicleImage => {
  const authFetch = useAuthFetchWithResponse()
  const queryKey = getVehicleImageQueryKey(installationId, vehicleId)
  const [imageUrl, setImageUrl] = useState<string | null>(null)

  const queryFn = async (): Promise<Blob | null> => {
    if (!installationId || !vehicleId) return null

    const { error, hasErrors, value } = await fetchVehicleImage(installationId, vehicleId, authFetch)

    if (hasErrors || error) {
      throw new Error(error?.message)
    }

    return value ?? null
  }

  const { data, isLoading, isError } = usePortalQuery({
    queryKey,
    queryFn,
    retry: MAX_RETRY_ATTEMPTS,
    /* retries once after 5 seconds */
    retryDelay: TimeInMilliseconds.fiveSecond,
    staleTime: TimeInMilliseconds.oneHour,
    enabled: !!installationId && !!vehicleId,
  })

  useEffect(() => {
    if (!data) {
      setImageUrl(null)
      return
    }
    const url = URL.createObjectURL(data)
    setImageUrl(url)

    /* Cleanup function to avoid memory leak */
    return () => {
      URL.revokeObjectURL(url)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (isError) setImageUrl(null)
  }, [isError])

  return { imageUrl, loading: isLoading, hasError: isError }
}
